// Safe job helpers for EMT side
import { ensureDb } from "./ensureDb";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit,
  startAfter,
  type QueryDocumentSnapshot,
  type DocumentData,
} from "firebase/firestore";
import type { Job } from "./types";
import { isHiddenDemoJob, isOffLimitsForPracticeViewer } from "../demoAccounts";
import { appCheckSettled } from "../firebase";

/**
 * THE rule for which jobs a candidate may see: open, active, not expired, not QA/demo/invalid.
 * Exported so every count of "open jobs" (company discovery, dashboards) uses exactly this rule —
 * the company map used to count isOpen==true alone and reported 12 open jobs when the board had 5.
 */
export function filterJobsForListing(allJobs: Job[]): Job[] {
  const now = new Date();
  // Production still relies on isSeedListing demos until real employer volume is enough.
  // Staging keeps seed exclusion; QA fixtures (isQATest) are hidden in every environment.
  const hideSeedListings = import.meta.env.VITE_FB_PROJECT_ID !== 'ready-responder-prod';
  return allJobs.filter((job) => {
    if ((job as any).isQATest === true) return false;
    if (isHiddenDemoJob(job)) return false;
    if (hideSeedListings && (job as any).isSeedListing === true) return false;
    if ((job as any).isInvalid === true) return false;
    const hasOrgId = typeof job.orgId === "string" && job.orgId.trim().length > 0;
    if (!hasOrgId) {
      console.warn('[JOBS_BAD_DATA] Job missing orgId:', { jobId: job.id, title: job.title });
      return false;
    }
    const status = (job as any).status || (job.isOpen ? "active" : "closed");
    if (status !== "active") return false;
    for (const deadlineField of ['applicationDeadline', 'expiresAt'] as const) {
      const raw = (job as Record<string, unknown>)[deadlineField];
      if (!raw) continue;
      try {
        const deadline =
          typeof (raw as { toDate?: () => Date }).toDate === 'function'
            ? (raw as { toDate: () => Date }).toDate()
            : new Date(raw as string | number);
        if (deadline && deadline < now) return false;
      } catch {
        console.warn('[JOBS_BAD_DATA] Invalid deadline for job:', { jobId: job.id, deadlineField });
      }
    }
    return true;
  });
}

const LIST_OPEN_JOBS_PAGINATED_MAX_CHUNKS = 30;

/**
 * Paginated open jobs (newest first). Fetches isOpen==true + orderBy(createdAt desc)
 * in fixed-size chunks, applies filterJobsForListing per doc, and advances the cursor
 * by the last doc in each chunk so in-batch filtering does not skip documents.
 */
export async function listOpenJobsPaginated(
  pageSize: number,
  cursor?: QueryDocumentSnapshot<DocumentData> | null
): Promise<{
  jobs: Job[];
  nextCursor: QueryDocumentSnapshot<DocumentData> | null;
  hasMore: boolean;
}> {
  const firestoreDb = ensureDb();
  const CHUNK = Math.max(20, pageSize);
  const collected: Job[] = [];
  let fsCursor: QueryDocumentSnapshot<DocumentData> | null = cursor ?? null;
  let lastDoc: QueryDocumentSnapshot<DocumentData> | null = null;
  let lastBatchFull = false;

  try {
    for (let n = 0; n < LIST_OPEN_JOBS_PAGINATED_MAX_CHUNKS && collected.length < pageSize; n++) {
      const q = fsCursor
        ? query(
            collection(firestoreDb, "jobs"),
            where("isOpen", "==", true),
            orderBy("createdAt", "desc"),
            startAfter(fsCursor),
            limit(CHUNK)
          )
        : query(
            collection(firestoreDb, "jobs"),
            where("isOpen", "==", true),
            orderBy("createdAt", "desc"),
            limit(CHUNK)
          );
      const snap = await getDocs(q);
      if (snap.empty) {
        lastBatchFull = false;
        break;
      }
      lastDoc = snap.docs[snap.docs.length - 1];
      lastBatchFull = snap.size === CHUNK;
      for (const d of snap.docs) {
        const job = { id: d.id, ...(d.data() as any) } as Job;
        if (filterJobsForListing([job]).length > 0) {
          collected.push(job);
          if (collected.length === pageSize) break;
        }
      }
      fsCursor = lastDoc;
      if (!lastBatchFull) break;
    }
    const jobs = collected.slice(0, pageSize);
    return {
      jobs,
      nextCursor: lastDoc,
      hasMore: lastBatchFull,
    };
  } catch {
    console.warn("[listOpenJobsPaginated] primary failed, using listOpenJobs fallback");
    const fallback = await listOpenJobs(pageSize);
    return { jobs: fallback, nextCursor: null, hasMore: false };
  }
}

/**
 * List open jobs (isOpen === true) with orgId
 * Filters out jobs missing orgId (broken data), expired jobs, and non-active status
 * Returns empty array on error
 */
export async function listOpenJobs(limitN?: number): Promise<Job[]> {
  const firestoreDb = ensureDb();
  const maxCount = limitN ?? 25;
  try {
    const q = query(
      collection(firestoreDb, "jobs"),
      where("isOpen", "==", true),
      orderBy("createdAt", "desc"),
      limit(maxCount * 2) // Fetch more to account for filtering
    );
    const snap = await getDocs(q);
    const allJobs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Job[];
    if (typeof console !== 'undefined' && console.log) {
      if (import.meta.env.DEV) console.log('[JOBS] listOpenJobs query returned', snap.size, 'docs');
    }
    const validJobs = filterJobsForListing(allJobs);

    const brokenJobs = allJobs.filter(j => !j.orgId);
    if (brokenJobs.length > 0) {
      console.warn('[JOBS_BAD_DATA] Found jobs missing orgId:', brokenJobs.map(j => ({ id: j.id, title: j.title })));
    }

    return validJobs.slice(0, maxCount);
  } catch (err) {
    try {
      const q = query(
        collection(firestoreDb, "jobs"),
        orderBy("createdAt", "desc"),
        limit(maxCount * 2)
      );
      const snap = await getDocs(q);
      const allJobs = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })) as Job[];
      const now = new Date();
      const validJobs = allJobs.filter((job) => {
        if ((job as any).isQATest === true) return false;
        if (isHiddenDemoJob(job)) return false;
        if (import.meta.env.VITE_FB_PROJECT_ID !== 'ready-responder-prod' && (job as any).isSeedListing === true) {
          return false;
        }
        if ((job as any).isInvalid === true) return false;
        const hasOrgId = typeof job.orgId === "string" && job.orgId.trim().length > 0;
        if (!hasOrgId) return false;
        const status = (job as any).status || (job.isOpen ? "active" : "closed");
        if (status !== "active") return false;
        if ((job as any).expiresAt) {
          try {
            const expiresAt = (job as any).expiresAt?.toDate ? (job as any).expiresAt.toDate() : new Date((job as any).expiresAt);
            if (expiresAt && expiresAt < now) return false;
          } catch {
            // Invalid date, skip expiration check
          }
        }
        return true;
      });
      return validJobs.slice(0, maxCount);
    } catch (fallbackErr) {
      console.error("[listOpenJobs] Error:", fallbackErr);
      return [];
    }
  }
}

/**
 * Get single job by ID
 * Returns null if not found or on error
 */
export async function getJob(jobId: string): Promise<Job | null> {
  const firestoreDb = ensureDb();
  try {
    const snap = await getDoc(doc(firestoreDb, "jobs", jobId));
    if (!snap.exists() || isOffLimitsForPracticeViewer(snap.data())) return null;
    return { id: snap.id, ...(snap.data() as any) } as Job;
  } catch (err) {
    console.error("[getJob] Error:", err);
    return null;
  }
}

/**
 * getJob for a page that must tell "this job does not exist" apart from "the read failed": resolves
 * null only when the document is missing, and THROWS on any error (getJob swallows errors into
 * null, which is how a network blip would read as "job removed").
 *
 * One exception: permission-denied on a job read means a saved draft (the only postings the rules
 * hide from a visitor) — for the public that IS "not available", so it resolves null. It is only
 * treated that way after a retry, because a request sent before the App Check token is ready is
 * refused with the very same code.
 */
export async function getJobStrict(jobId: string, attempts = 3): Promise<Job | null> {
  const firestoreDb = ensureDb();
  await appCheckSettled; // a signed-out page's first read must not race App Check start-up

  let lastError: unknown = null;
  for (let attempt = 1; attempt <= attempts; attempt++) {
    try {
      const snap = await getDoc(doc(firestoreDb, "jobs", jobId));
      return snap.exists() && !isOffLimitsForPracticeViewer(snap.data()) ? ({ id: snap.id, ...(snap.data() as any) } as Job) : null;
    } catch (err) {
      lastError = err;
      if (attempt < attempts) await new Promise((r) => setTimeout(r, 1200 * attempt));
    }
  }
  if ((lastError as { code?: string } | null)?.code === "permission-denied") return null;
  throw lastError;
}

/**
 * One employer's open jobs for the PUBLIC job board (/careers/:orgId) and the public company
 * profile — works signed out. The `isOpen == true` constraint is not optional: firestore.rules only
 * let a signed-out visitor list jobs through a query that asks for open ones, so drafts and closed
 * postings can never be enumerated. Newest activity first.
 *
 * Unlike the candidate board this does NOT hide an employer's demo jobs — it is that employer's own
 * page, reached by their own link. QA fixtures, invalid rows, drafts and expired postings never show.
 * Throws on a read failure so the page can say "couldn't load" instead of "no open jobs".
 */
export async function listPublicOrgJobs(orgId: string, limitN = 50): Promise<Job[]> {
  const firestoreDb = ensureDb();
  await appCheckSettled;
  const snap = await getDocs(
    query(
      collection(firestoreDb, "jobs"),
      where("orgId", "==", orgId),
      where("isOpen", "==", true),
      orderBy("updatedAt", "desc"),
      limit(limitN)
    )
  );
  const now = new Date();
  return snap.docs
    .map((d) => ({ id: d.id, ...(d.data() as any) }) as Job)
    .filter((job) => {
      const row = job as Record<string, unknown>;
      if (row.isQATest === true || row.isInvalid === true) return false;
      const status = (row.status as string | undefined) || "active";
      if (status !== "active" && status !== "emergency") return false;
      for (const deadlineField of ["applicationDeadline", "expiresAt"] as const) {
        const raw = row[deadlineField];
        if (!raw) continue;
        try {
          const deadline =
            typeof (raw as { toDate?: () => Date }).toDate === "function"
              ? (raw as { toDate: () => Date }).toDate()
              : new Date(raw as string | number);
          if (deadline && deadline < now) return false;
        } catch {
          // an unreadable deadline is not a reason to hide the job
        }
      }
      return true;
    });
}
